import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import AuthModal from './components/auth/AuthModal';
import { Loader } from 'lucide-react';

// Critical pages - loaded immediately
import HomePage from './pages/HomePage';
import AppDetailPage from './pages/AppDetailPage';

// Non-critical pages - lazy loaded
const WeekViewPage = lazy(() => import('./pages/WeekViewPage'));
const SubmitAppPage = lazy(() => import('./pages/SubmitAppPage'));
const CreatorDashboard = lazy(() => import('./pages/CreatorDashboard'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const ContestPage = lazy(() => import('./pages/ContestPage'));

import './App.css';

// Loading fallback component
function LoadingFallback() {
  return (
    <div className="min-h-screen bg-dark-bg flex items-center justify-center">
      <div className="text-center">
        <Loader className="w-8 h-8 text-brand-yellow animate-spin mx-auto mb-4" />
        <p className="text-gray-400">Loading...</p>
      </div>
    </div>
  );
}

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname]);

  return null;
}

// Inner component to access auth context
function AppContent() {
  const { showAuthModal, closeAuthModal } = useAuth();

  return (
    <>
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1">
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/apps/:id" element={<AppDetailPage />} />
              <Route path="/week/:weekId" element={<WeekViewPage />} />
              <Route path="/submit" element={<SubmitAppPage />} />
              <Route path="/dashboard" element={<CreatorDashboard />} />
              <Route path="/admin" element={<AdminPanel />} />
              <Route path="/contest" element={<ContestPage />} />
              <Route path="/how-it-works" element={<ContestPage />} />
            </Routes>
          </Suspense>
        </main>
        <Footer />
      </div>
      
      {/* Global Auth Modal */}
      {showAuthModal && (
        <AuthModal
          isOpen={showAuthModal}
          onClose={closeAuthModal}
        />
      )}
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <AppContent />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
